import { useState, useRef, useCallback } from 'react';
import { Company, PostgresConfig, User } from '../types';
import { getPostgresClient } from '../services/postgresClient';
import { loadFullCompanyData } from '../services/rhDataService';
import { sortItemsByEmployeeName } from '../utils/employeeSort';
import { useNotifications } from './useNotifications';
import { useBoardSync } from './useBoardSync';
import { useBoardSettings } from './useBoardSettings';
import { useIntegrationSettings } from './useIntegrationSettings';
import { useAuditLog } from './useAuditLog';
import { useBoardEmployees } from './useBoardEmployees';

export interface UseBoardDataParams {
  isAuthenticated: boolean;
  currentCompany: Company | null;
  currentUser: User | null;
  getVerifiedDate: () => Date;
}

/**
 * Orquestra sub-hooks do board: sync, colaboradores, settings, integrações e auditoria.
 * Mantém a API pública estável para App.tsx e AppSettingsPanel.
 */
export function useBoardData({
  isAuthenticated,
  currentCompany,
  currentUser,
  getVerifiedDate,
}: UseBoardDataParams) {
  const [postgresConfig, setPostgresConfig] = useState<PostgresConfig>({ url: '' });
  const [currentCompanyId, setCurrentCompanyId] = useState<string | null>(null);
  const isDataLoadedRef = useRef(false);
  const [isBoardLoading, setIsBoardLoading] = useState(false);

  const isAdmin = Boolean(currentUser?.permissions.settings);

  const {
    notifications,
    addNotification,
    broadcastNotifications,
    dismissNotification,
    clearNotifications,
    resetNotifications,
  } = useNotifications({
    getVerifiedDate,
    isAuthenticated,
    currentCompanyId,
    isAdmin,
    postgresApiUrl: postgresConfig.url,
  });

  const getSB = useCallback(() => getPostgresClient(postgresConfig.url), [postgresConfig.url]);

  const settings = useBoardSettings({
    isAuthenticated,
    currentUser,
    currentCompany,
    currentCompanyId,
    isDataLoadedRef,
    getSB,
  });

  const audit = useAuditLog({
    currentUser,
    currentCompanyId,
    getSB,
    getVerifiedDate,
  });

  const employees = useBoardEmployees({
    columns: settings.columns,
    afastamentoDates: settings.afastamentoDates,
    setAfastamentoDates: settings.setAfastamentoDates,
    currentCompany,
    currentCompanyId,
    getSB,
    persistSettingIfAuthenticated: settings.persistSettingIfAuthenticated,
    getVerifiedDate,
    addLog: audit.addLog,
    addNotification,
    broadcastNotifications,
  });

  const sync = useBoardSync({
    isAuthenticated,
    currentCompanyId,
    isDataLoadedRef,
    postgresConfig,
    setItems: employees.setItems,
    setColumnKeyMap: employees.setColumnKeyMap,
  });

  const integrations = useIntegrationSettings({
    hasSettingsPermission: settings.hasSettingsPermission,
    getSB,
    postgresApiUrl: postgresConfig.url,
    persistSettingIfAuthenticated: settings.persistSettingIfAuthenticated,
    canPersistSettings: settings.canPersistSettings,
    addNotification,
  });

  const resetBoard = useCallback(() => {
    isDataLoadedRef.current = false;
    setIsBoardLoading(false);
    setCurrentCompanyId(null);
    resetNotifications();
    employees.resetEmployeeState();
    settings.resetSettingsState();
    audit.resetAuditState();
    integrations.resetIntegrationState();
  }, [resetNotifications, employees, settings, audit, integrations]);

  const loadCompanyAfterLogin = useCallback(
    async (_user: User, company: Company): Promise<boolean> => {
      const sb = getSB();
      if (!sb) {
        addNotification(
          'API Indisponivel',
          'API do Postgres indisponivel. Nao ha fallback local neste ambiente.',
          'delete',
        );
        return false;
      }

      isDataLoadedRef.current = false;
      setIsBoardLoading(true);
      if (_user.permissions.settings) audit.setLogs([]);
      try {
        const data = await loadFullCompanyData(sb, company);
        setCurrentCompanyId(data.companyId);
        employees.setItems(sortItemsByEmployeeName(data.items));
        settings.setColumns(data.columns);
        settings.setHiringLimits(data.hiringLimits);
        settings.setAvailableRoles(data.availableRoles.sort((a, b) => a.localeCompare(b, 'pt-BR')));
        settings.setRoleColors(data.roleColors);
        employees.setColumnKeyMap(data.columnKeyMap);
        settings.setAfastamentoDates(data.afastamentoDates || {});
        settings.setCostCenters(data.costCenters ?? []);

        if (_user.permissions.settings && data.companyId) {
          await audit.loadAuditDataFromDb(sb, data.companyId);
          await integrations.loadIntegrationSettingsFromDb(sb);
        }

        await integrations.loadAppVersionFromDb(sb);
        sync.setIsDbConnected(true);
        isDataLoadedRef.current = true;
        return true;
      } catch (err) {
        console.error('[Postgres] Erro ao carregar dados da empresa:', err);
        sync.setIsDbConnected(false);
        const message = err instanceof Error ? err.message : '';
        const isPermissionError =
          message.includes('Sem permissão') || message.includes('403');
        addNotification(
          isPermissionError ? 'Sem Permissão' : 'Erro de Conexao',
          isPermissionError
            ? message || 'Seu usuário não tem permissão para esta operação.'
            : 'Falha ao carregar dados do Postgres. Verifique se a API está online.',
          'delete',
        );
        return false;
      } finally {
        setIsBoardLoading(false);
      }
    },
    [getSB, addNotification, audit, employees, settings, integrations, sync],
  );

  const isAdministrative = currentCompany === 'GSan (ADMINISTRATIVO)';

  return {
    postgresConfig,
    setPostgresConfig,
    currentCompanyId,
    setCurrentCompanyId,
    items: employees.items,
    setItems: employees.setItems,
    columns: settings.columns,
    setColumns: settings.setColumns,
    columnKeyMap: employees.columnKeyMap,
    setColumnKeyMap: employees.setColumnKeyMap,
    hiringLimits: settings.hiringLimits,
    setHiringLimits: settings.setHiringLimits,
    availableRoles: settings.availableRoles,
    setAvailableRoles: settings.setAvailableRoles,
    roleColors: settings.roleColors,
    setRoleColors: settings.setRoleColors,
    afastamentoDates: settings.afastamentoDates,
    setAfastamentoDates: settings.setAfastamentoDates,
    costCenters: settings.costCenters,
    setCostCenters: settings.setCostCenters,
    isDbConnected: sync.isDbConnected,
    connectionState: sync.connectionState,
    logs: audit.logs,
    notifications,
    users: audit.users,
    setUsers: audit.setUsers,
    messagingConfig: integrations.messagingConfig,
    setMessagingConfig: integrations.setMessagingConfig,
    mondayConfig: integrations.mondayConfig,
    setMondayConfig: integrations.setMondayConfig,
    mondayLogs: integrations.mondayLogs,
    appVersionInfo: integrations.appVersionInfo,
    setAppVersionInfo: integrations.setAppVersionInfo,
    isSavingAppVersion: integrations.isSavingAppVersion,
    getSB,
    persistSettingIfAuthenticated: settings.persistSettingIfAuthenticated,
    addNotification,
    addLog: audit.addLog,
    refreshMondayLogs: integrations.refreshMondayLogs,
    saveAppVersionInfo: integrations.saveAppVersionInfo,
    resetBoard,
    loadCompanyAfterLogin,
    broadcastNotifications,
    dismissNotification,
    clearNotifications,
    handleSaveEmployee: employees.handleSaveEmployee,
    handleUpdateAbsence: employees.handleUpdateAbsence,
    confirmDelete: employees.confirmDelete,
    isAdministrative,
    isBoardLoading,
  };
}
